import { useRef, useEffect } from 'react';

import Box from '@mui/material/Box';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useHasPermission } from 'src/hooks/use-has-permission';

import { useGetStoreModulePermissionsQuery } from 'src/graphql/generated';

import { useSettingsContext } from 'src/components/settings';
import IntercomWidget from 'src/components/intercom/intercom-widget';

import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: Props) {
  const settings = useSettingsContext();

  const pathname = usePathname();

  const wasJornadasRoute = useRef(false);

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();

  const isJornadasRoute =
    pathname === paths.dashboard.jornadas.root ||
    pathname.startsWith(`${paths.dashboard.jornadas.root}/`);

  const isJourneyEditor =
    isJornadasRoute && /^\/dashboard\/jornadas\/[^/]+\/edit\/?$/.test(pathname);

  const { data: profileData, hasAdminPermission, hasEmployeePermission } = useHasPermission();
  const { data: modulePermissionsData } = useGetStoreModulePermissionsQuery({
    skip: !isJourneyEditor,
  });
  const canUseJourneyEditor =
    profileData?.me?.connectedStore?.isActive &&
    (hasAdminPermission || hasEmployeePermission) &&
    modulePermissionsData?.getStoreModulePermissions?.hasJourneys;

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  useEffect(() => {
    if (
      isJornadasRoute &&
      !isJourneyEditor &&
      !wasJornadasRoute.current &&
      settings.themeLayout !== 'mini'
    ) {
      settings.onUpdate('themeLayout', 'mini');
    }

    wasJornadasRoute.current = isJornadasRoute;
  }, [isJornadasRoute, isJourneyEditor, settings]);

  const renderNavMini = <NavMini />;

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  if (isJourneyEditor && canUseJourneyEditor) {
    return <Main fullScreen>{children}</Main>;
  }

  if (isHorizontal) {
    return (
      <>
        <IntercomWidget />

        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>{children}</Main>
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <IntercomWidget />

        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main>{children}</Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <IntercomWidget />

      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {renderNavVertical}

        <Main>{children}</Main>
      </Box>
    </>
  );
}
